import { Link } from 'react-router-dom'
import { Loading } from 'react-daisyui'
import { useQuery } from '@tanstack/react-query'
import { IconAlertTriangle, IconMovie } from '@tabler/icons-react'
import { useClient } from '@/stores/useClient.ts'
import { formatBytes, ProfStorageResponse, VideoToDelete } from '@/models/profStorage.ts'

export const PROF_STORAGE_QUERY_KEY = ['portal', 'storage']
const PENDING_DELETION_QUERY_KEY = ['portal', 'storage', 'pending-deletion']

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
}

/**
 * Settings → "Speicher": video storage (the only limited part, 10 GB by default), image and
 * total usage as information, and all projects by usage. When over the video limit, it also
 * lists the newest videos that get deleted automatically 7 days after the overage was detected.
 */
export default function StorageSettingsSection() {
  const { getStorage } = useClient()
  const { data: storage, isLoading } = useQuery({
    queryKey: PROF_STORAGE_QUERY_KEY,
    queryFn: async () => (await getStorage()).data,
  })

  if (isLoading || !storage) {
    return (
      <section className="bg-neutral p-5 max-w-2xl">
        <Loading size="sm" />
      </section>
    )
  }

  return (
    <div className="flex flex-col gap-4 max-w-3xl">
      <VideoStorageCard storage={storage} />
      {storage.video.is_over_limit && <PendingDeletionCard storage={storage} />}
      <ProjectsStorageCard storage={storage} />
    </div>
  )
}

function VideoStorageCard({ storage }: { storage: ProfStorageResponse }) {
  const { video, image, total } = storage
  const percent = video.limit > 0 ? Math.min(100, (video.used / video.limit) * 100) : 100
  const progressColor = video.is_over_limit ? 'progress-error' : percent >= 90 ? 'progress-warning' : 'progress-primary'

  return (
    <section className="bg-neutral border border-[var(--portal-border)] p-5">
      <h2 className="font-serif text-xl mb-1">Videospeicher</h2>
      <p className="text-sm opacity-60 mb-4">
        In deinem Konto sind {formatBytes(video.limit)} für Videos enthalten. Bilder sind nicht begrenzt.
      </p>

      <div className="flex items-baseline justify-between gap-3 mb-1 flex-wrap">
        <span className="text-lg font-medium">
          {formatBytes(video.used)} <span className="opacity-50 text-sm font-normal">von {formatBytes(video.limit)}</span>
        </span>
        <span className={`text-sm ${video.is_over_limit ? 'text-error font-medium' : 'opacity-60'}`}>
          {video.is_over_limit
            ? `${formatBytes(video.used - video.limit)} zu viel`
            : `${formatBytes(video.remaining)} frei`}
        </span>
      </div>
      <progress className={`progress ${progressColor} w-full`} value={percent} max={100} />
      <p className="text-xs opacity-50 mt-1">
        {video.count} {video.count === 1 ? 'Video' : 'Videos'}
      </p>

      <div className="flex gap-6 mt-4 text-sm flex-wrap">
        <div>
          <p className="text-xs uppercase tracking-wider opacity-60 font-semibold">Bilder</p>
          <p>{formatBytes(image.used)} <span className="opacity-50">· {image.count} {image.count === 1 ? 'Bild' : 'Bilder'}</span></p>
        </div>
        <div>
          <p className="text-xs uppercase tracking-wider opacity-60 font-semibold">Gesamt</p>
          <p>{formatBytes(total.used)}</p>
        </div>
      </div>
    </section>
  )
}

function PendingDeletionCard({ storage }: { storage: ProfStorageResponse }) {
  const { getStoragePendingDeletion } = useClient()
  const { data, isLoading } = useQuery({
    queryKey: PENDING_DELETION_QUERY_KEY,
    queryFn: async () => (await getStoragePendingDeletion()).data,
  })

  const deletionAt = storage.video_storage_deletion_at

  return (
    <section className="bg-neutral border border-error p-5">
      <h2 className="font-serif text-xl mb-2 flex items-center gap-2 text-error">
        <IconAlertTriangle size={20} />
        Videospeicher überschritten
      </h2>
      <p className="text-sm mb-1">
        {deletionAt
          ? <>Am <strong>{formatDate(deletionAt)}</strong> werden automatisch die neuesten Videos gelöscht, bis du wieder in deinem Speicher liegst.</>
          : <>Werden keine Videos gelöscht, werden 7 Tage nach der nächsten Prüfung automatisch die neuesten Videos gelöscht. Du erhältst dazu eine E-Mail.</>}
      </p>
      <p className="text-sm opacity-60 mb-4">
        Möchtest du lieber andere Videos behalten, lösche selbst mindestens {formatBytes(storage.video.used - storage.video.limit)} an Videos.
        Dann wird nichts automatisch gelöscht.
      </p>

      {isLoading && <Loading size="sm" />}
      {data && data.files.length > 0 && (
        <>
          <p className="text-xs uppercase tracking-wider opacity-60 font-semibold mb-2">
            Werden gelöscht: {data.files.length} {data.files.length === 1 ? 'Video' : 'Videos'} · {formatBytes(data.freed)}
          </p>
          <div className="flex flex-col divide-y divide-[var(--portal-border)] border-y border-[var(--portal-border)]">
            {data.files.map(file => <VideoToDeleteRow key={file.id} file={file} />)}
          </div>
        </>
      )}
    </section>
  )
}

function VideoToDeleteRow({ file }: { file: VideoToDelete }) {
  const content = (
    <>
      <div className="w-14 h-10 bg-secondary rounded-lg overflow-hidden flex items-center justify-center shrink-0">
        {file.thumbnail
          ? <img src={file.thumbnail} alt="" className="w-full h-full object-cover" />
          : <IconMovie size={18} className="opacity-40" />}
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm truncate" title={file.file_name}>{file.file_name}</p>
        <p className="text-xs opacity-50 truncate">
          {[file.project?.title, file.album.title].filter(Boolean).join(' · ')} · hochgeladen am {formatDate(file.created_at)}
        </p>
      </div>
      <span className="text-sm opacity-60 whitespace-nowrap">{formatBytes(file.size)}</span>
    </>
  )

  return file.project
    ? <Link to={`/portal/projects/${file.project.id}`} className="flex items-center gap-3 py-2 px-2 hover:bg-secondary">{content}</Link>
    : <div className="flex items-center gap-3 py-2 px-2">{content}</div>
}

function ProjectsStorageCard({ storage }: { storage: ProfStorageResponse }) {
  const projects = storage.projects.filter(p => p.storage_usage.total.used > 0)

  return (
    <section className="bg-neutral border border-[var(--portal-border)] p-3 md:p-6">
      <h2 className="font-serif text-xl mb-4">Speicher pro Projekt</h2>
      {projects.length === 0 ? (
        <p className="text-sm opacity-60">Noch keine Dateien hochgeladen.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="table table-sm">
            <thead>
              <tr>
                <th className="text-xs uppercase tracking-wider opacity-60 font-semibold">Projekt</th>
                <th className="text-xs uppercase tracking-wider opacity-60 font-semibold text-right">Videos</th>
                <th className="text-xs uppercase tracking-wider opacity-60 font-semibold text-right">Bilder</th>
                <th className="text-xs uppercase tracking-wider opacity-60 font-semibold text-right">Gesamt</th>
              </tr>
            </thead>
            <tbody>
              {projects.map(project => {
                const usage = project.storage_usage
                return (
                  <tr key={project.id} className="hover:bg-secondary">
                    <td>
                      <Link to={`/portal/projects/${project.id}`} className="hover:underline">
                        {project.title || 'Ohne Titel'}
                      </Link>
                    </td>
                    <td className="text-right whitespace-nowrap">
                      {usage.video.count > 0
                        ? <>{formatBytes(usage.video.used)} <span className="opacity-50">· {usage.video.count}</span></>
                        : <span className="opacity-50">–</span>}
                    </td>
                    <td className="text-right whitespace-nowrap opacity-70">{formatBytes(usage.image.used)}</td>
                    <td className="text-right whitespace-nowrap opacity-70">{formatBytes(usage.total.used)}</td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      )}
    </section>
  )
}
